import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useProjects } from '@/api/hooks';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DangerZoneCard } from '@/features/project-settings/DangerZoneCard';
import { EnvironmentsCard } from '@/features/project-settings/EnvironmentsCard';
import { GeneralCard } from '@/features/project-settings/GeneralCard';
import { LimitsCard } from '@/features/project-settings/LimitsCard';
import { SettingsHeader } from '@/features/project-settings/SettingsHeader';
import { SettingsNav } from '@/features/project-settings/SettingsNav';
import { sectionAnchor, sectionCounts, type ProjectSettingsSearch, type ProjectSettingsSection } from '@/features/project-settings/settingsSections';
import { useScrollToSection } from '@/features/project-settings/useScrollToSection';

// The router reads these from here.
export { PROJECT_SETTINGS_SECTIONS, type ProjectSettingsSearch, type ProjectSettingsSection } from '@/features/project-settings/settingsSections';

/**
 * Design 28 · Project settings: title with the slug and status, a section nav (a column from 1280 px, one scrolling row
 * below) and one card per section — General, Environments, Members (soon: no API), Limits, Danger zone. `?section=`
 * scrolls to a card, so a link can open the page on its environments.
 */
export function ProjectSettingsPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const search = useSearch({ strict: false }) as ProjectSettingsSearch;
  const navigate = useNavigate();
  const projects = useProjects();
  const project = projects.data?.find((p) => p.slug === slug);
  useScrollToSection(project?.id, search.section);

  if (projects.isLoading && !projects.data) return <SettingsSkeleton />;
  if (projects.isError && !projects.data) return <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />;
  if (!project) return <ProjectNotFound slug={slug} />;

  const go = (s: ProjectSettingsSection) => void navigate({ to: '/projects/$slug/settings', params: { slug }, search: { section: s }, replace: true, resetScroll: false });
  return (
    <div className="flex flex-col gap-6" data-testid="project-settings">
      <SettingsHeader project={project} />
      <div className="grid gap-6 xl:grid-cols-[208px_minmax(0,1fr)] xl:items-start">
        <SettingsNav section={search.section ?? 'general'} counts={sectionCounts(project)} onGo={go} />
        <div className="flex min-w-0 flex-col gap-6">
          <GeneralCard project={project} id={sectionAnchor('general')} index={1} />
          <EnvironmentsCard project={project} id={sectionAnchor('environments')} index={2} />
          <LimitsCard project={project} id={sectionAnchor('limits')} index={4} />
          <DangerZoneCard project={project} id={sectionAnchor('danger')} index={5} />
        </div>
      </div>
    </div>
  );
}

function ProjectNotFound({ slug }: { slug: string }) {
  return (
    <EmptyState
      icon="account_tree"
      title="Project not found"
      description={`There is no live project with slug “${slug}” in this organization.`}
      action={
        <Button asChild variant="outline">
          <Link to="/">Back to Overview</Link>
        </Button>
      }
    />
  );
}

function SettingsSkeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true" aria-label="Loading project settings">
      <SkeletonHeader />
      <div className="grid gap-6 xl:grid-cols-[208px_minmax(0,1fr)]">
        <div className="flex gap-2 xl:flex-col">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-9 w-28 xl:w-full" />
          ))}
        </div>
        <SkeletonCard />
      </div>
    </div>
  );
}

function SkeletonHeader() {
  return (
    <div className="flex flex-col gap-3 border-b pb-6">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-6 w-20" />
        <Skeleton className="h-6 w-28" />
      </div>
      <Skeleton className="h-4 w-80" />
    </div>
  );
}

function SkeletonCard() {
  return (
    <Card>
      <CardHeader className="gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-72" />
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <Skeleton className="h-14" />
        <Skeleton className="h-14" />
        <Skeleton className="h-20 sm:col-span-2" />
      </CardContent>
    </Card>
  );
}
